import { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  useEligibleAdvisorReferralClients,
  useReferAdvisor,
} from '../hooks/useAdvisorReferrals';
import type { EligibleAdvisorReferralClient } from '../types/advisor-referrals.types';

type ReferAdvisorModalProps = {
  open: boolean;
  financialAdvisorId: string;
  advisorName: string;
  onOpenChange: (open: boolean) => void;
};

type ApiError = {
  message?: string;
  response?: { data?: { message?: string } };
};

const getClientName = (client: EligibleAdvisorReferralClient) =>
  `${client.firstName} ${client.lastName ?? ''}`.trim();

const getErrorMessage = (error: unknown): string => {
  const apiError = error as ApiError;
  return apiError.response?.data?.message ?? apiError.message ?? 'Unable to refer advisor';
};

export const ReferAdvisorModal = ({
  open,
  financialAdvisorId,
  advisorName,
  onOpenChange,
}: ReferAdvisorModalProps) => {
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const eligibleClientsQuery = useEligibleAdvisorReferralClients(open);
  const referAdvisor = useReferAdvisor();
  const clients = eligibleClientsQuery.data?.clients ?? [];
  const canSubmit = selectedClientIds.length > 0 && !referAdvisor.isPending;

  const toggleClient = (clientId: string, checked: boolean) => {
    setSubmitError(null);
    setSelectedClientIds((current) =>
      checked ? [...current, clientId] : current.filter((id) => id !== clientId),
    );
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await referAdvisor.mutateAsync({
        financialAdvisorId,
        careSeekerIds: selectedClientIds,
        message: message.trim() || undefined,
      });
      setSelectedClientIds([]);
      setMessage('');
      onOpenChange(false);
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refer Advisor</DialogTitle>
          <DialogDescription>
            Select eligible clients to refer to {advisorName}.
          </DialogDescription>
        </DialogHeader>

        {eligibleClientsQuery.isLoading && (
          <div className="rounded-md border border-dashed p-4 text-sm text-gray-600">
            Loading eligible clients...
          </div>
        )}

        {eligibleClientsQuery.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium">Unable to load eligible clients</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => eligibleClientsQuery.refetch()}>
              Try Again
            </Button>
          </div>
        )}

        {!eligibleClientsQuery.isLoading && !eligibleClientsQuery.isError && clients.length === 0 && (
          <div className="rounded-md border border-dashed p-4 text-sm text-gray-600">
            All your clients already have a Financial Advisor
          </div>
        )}

        {clients.length > 0 && (
          <div className="space-y-3">
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {clients.map((client) => {
                const label = `${getClientName(client)} (${client.email})`;
                return (
                  <label
                    key={client.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md border p-3"
                  >
                    <Checkbox
                      checked={selectedClientIds.includes(client.id)}
                      onCheckedChange={(checked) => toggleClient(client.id, checked === true)}
                      aria-label={label}
                    />
                    <span>
                      <span className="block font-medium text-gray-900">{getClientName(client)}</span>
                      <span className="block text-sm text-gray-600">{client.email}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <label className="block text-sm font-medium text-gray-700">
              Optional message
              <Textarea
                className="mt-2"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={1000}
              />
            </label>
          </div>
        )}

        {submitError && <p className="text-sm text-red-600">{submitError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {referAdvisor.isPending ? 'Referring...' : 'Refer Advisor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
